/**
 * Egzersiz ekranı.
 *
 * Çizim modelinin ikinci tüketicisi: yazdırma oluşturucusu aynı düğümleri
 * A4'e basıyor, burası ekrana çiziyor ve etkileşimli hale getiriyor.
 *
 * Ama her egzersiz aynı şekilde cevaplanmıyor. Dört etkileşim biçimi var
 * ve hangisinin kullanılacağı şablona göre belirleniyor:
 *
 *   isaretle  hücrelere/seçeneklere dokunarak işaretle (varsayılan)
 *   yaz       kutulara yaz; şekil isteniyorsa klavye yerine palet
 *   damgala   paletten bir şekil seç, hücrelere bas
 *   cevir     kapalı kartları çevirerek eşleştir
 *
 * Bunları tek bir genel çizim döngüsüne sığdırmaya çalışmak, ekranda
 * cevaplanamayan egzersizler üretiyordu: şekil dizisini klavyeyle yazmak,
 * zaten açık duran kartları "çevirmek", hiç çalınmayan sesi dinlemek gibi.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Dizi, Uyaran, UyaranAdi, kimlik } from '../components/Sekil'
import {
  EXERCISE_CODES,
  EXERCISES,
  generateExercise,
  getExercise,
  type ExerciseCode,
  type ExerciseInstance,
  type GradeResult,
  type RenderNode,
} from '@nirengi/engine'

type Mode = 'isaretle' | 'yaz' | 'damgala' | 'cevir' | 'cizgi' | 'kelime' | 'dizi' | 'sihirbaz'

/**
 * "Bunu ekranda nasıl yapacağım" satırı.
 *
 * Motorun ürettiği yönerge GÖREVİ anlatır ve kâğıtta da geçerlidir;
 * burada eklenen satır EKRANDA nasıl cevaplanacağını anlatır. İkisi
 * ayrı tutuluyor çünkü kâğıtta çocuk çiziyor, ekranda dokunuyor —
 * aynı cümle ikisine birden uymuyor.
 */
const EXERCISE_HINT: Record<ExerciseCode, string> = {
  scan_target:
    'Şekli gördüğün her kutuya dokun. Soldan sağa, satır satır ilerlersen hiçbirini kaçırmazsın.',
  spot_difference:
    'Soldaki tablo örnek, ona dokunamazsın. Sağdakinde farklı olan kutulara dokun.',
  odd_one_out:
    'Her satırda sadece bir tane farklı var. O satırı bitirmeden diğerine geçme.',
  word_search:
    'Kelimeyi bulunca önce ilk harfine, sonra son harfine dokun. Kelimeler düz, dik ya da çapraz durabilir.',
  path_trace:
    'Çizgiyi gözünle takip et; çizgiler birbirinin üstünden geçiyor, karıştırma. Sonra kutuya sayıyı yaz.',
  span_recall:
    'Önce "Diziyi göster" düğmesine bas. Dizi bitince aşağıdaki şekillere sırayla dokunarak cevabını yaz.',
  memory_pairs:
    'Her seferinde iki kart açabilirsin. Aynıysa açık kalır, değilse kapanır. Kapanan kartların yerini aklında tut.',
  symbol_code:
    'Yukarıdaki anahtarda hangi şeklin hangi sayı olduğu yazıyor. Sırayla ilerle, hiçbirini atlama.',
  listening_steps:
    'Önce "Dinle" düğmesine bas; yönerge bir kez söylenir. Sonra aşağıdan bir şekil seç ve kutuya dokun.',
  reading_target:
    'Metni baştan sona oku. Kelimeyi her gördüğünde say, sonra toplam sayıyı kutuya yaz.',
  matrix_pattern:
    'Satırlara ve sütunlara bak, şekillerin sırası bir kurala uyuyor. Kuralı bulursan boş kutuya ne geleceğini anlarsın.',
  sequence_complete:
    'Sayılar arasında ne kadar fark olduğuna bak. Aynı kural sonuna kadar devam ediyor.',
  logic_grid:
    'İpuçlarını tek tek oku. En kısa olanı 1. kutuya, en uzun olanı sonuncuya yaz.',
  sudoku_mini:
    'Önce aşağıdan bir sayı seç, sonra boş kutuya dokun. Koyu renkli kutular hazır verilmiş, onlar değişmez.',
  category_sort:
    'Kelimeler tek tek gelecek. Her kelime için aşağıdaki gruplardan birine dokun.',
  maze: "A'dan başla ve komşu kutulara dokunarak ilerle. Duvardan geçemezsin. Yanlış gittiysen bir önceki kutuya dokun, geri alır.",
  mental_rotation:
    'Şekli kafanda çevirmeye çalış. Aradığın, aynadaki hali değil; çevrilmiş hali.',
  mirror_image:
    'Sayfayı ortadan katlasan üst üste gelecek şekilde düşün. Sağ taraftaki kutulara dokun.',
  arith_fluency:
    'Sırayla ilerle. Takıldığın işlemi atla, sonra geri dön.',
  opposite_rule:
    'Ok yukarı bakıyorsa aşağı, sağa bakıyorsa sola yazacaksın. Cevapları aşağıdaki oklardan seçiyorsun.',
}

function modeOf(code: ExerciseCode, instance: ExerciseInstance): Mode {
  if (code === 'memory_pairs') return 'cevir'
  if (code === 'span_recall') return 'dizi'
  if (code === 'category_sort') return 'sihirbaz'
  if (code === 'word_search') return 'kelime'
  if (code === 'maze') return 'cizgi'
  if (code === 'listening_steps' || code === 'sudoku_mini') return 'damgala'
  const hasRows = instance.render.some((n) => n.t === 'rows' && n.items.some((i) => i.blank))
  return hasRows ? 'yaz' : 'isaretle'
}

/** Sunucudan gelen ödev tarifi */
export interface OdevTarifi {
  id: string
  kod: ExerciseCode
  duzey: number
  tohum: string
  bitti: boolean
}

export function Exercise({
  onExit,
  odev,
  onGonder,
  listeVar = false,
}: {
  onExit: () => void
  /**
   * Bağlı mod: uygulayıcının verdiği ödev.
   *
   * Verilirse şablon seçici ve seviye düğmeleri görünmüyor — çocuğun
   * önüne menü koymanın anlamı yok, bağlantı zaten o ödev için verildi.
   * Sayfa sunucunun gönderdiği TOHUMDAN üretiliyor; böylece puanlarken
   * sunucu aynı sayfayı yeniden üretip aynı şeyi görüyor.
   */
  odev?: OdevTarifi
  /** Ham cevabı sunucuya yollar; sonuç çocuğa gösterilmez */
  onGonder?: (cevap: unknown, sureMs: number) => Promise<void>
  /**
   * Arkada bir liste var mı — haftalık bağlantıdan gelindiyse var.
   *
   * Bitiş ekranı tek ödevlik bağlantı (`?e=`) için yazılmıştı ve
   * "bu sayfayı kapatabilirsin" diyordu. Haftalık bağlantıda (`?h=`)
   * aynı ekran çıkmazdı sona çeviriyordu: çocuğun önünde dört çalışma
   * daha duruyor ama geri dönecek yer yok, uygulayıcının QR'ı yeniden
   * okutması gerekiyordu. Haftalık bağlantının var oluş sebebi tek
   * okutmaydı; o yüzden bitiş ekranı listeye dönüşü göstermeli.
   */
  listeVar?: boolean
}) {
  const bagli = Boolean(odev)
  const [code, setCode] = useState<ExerciseCode | null>(odev?.kod ?? null)
  const [level, setLevel] = useState(odev?.duzey ?? 5)
  const [round, setRound] = useState(0)
  const [result, setResult] = useState<GradeResult | null>(null)
  const [gonderimDurumu, setGonderimDurumu] = useState<'bekliyor' | 'gidiyor' | 'bitti' | 'hata'>(
    'bekliyor',
  )
  const baslangic = useRef<number>(Date.now())

  const instance = useMemo<ExerciseInstance | null>(
    () =>
      code
        ? generateExercise(code, odev ? BigInt(odev.tohum) : hashOf(code, level, round), level)
        : null,
    [code, level, round, odev],
  )

  /**
   * Bağlı modda cevap sunucuya gidiyor ve sonuç ÇOCUĞA GÖSTERİLMİYOR.
   *
   * Egzersiz bir sınav değil; "12'de 7" görmek çalışmayı performansa
   * çeviriyor. Çocuk bitirdiğini görür, değerlendirmeyi uygulayıcı yapar.
   */
  const sonucAl = (r: GradeResult, cevap: unknown) => {
    if (!bagli) {
      setResult(r)
      return
    }
    setGonderimDurumu('gidiyor')
    void onGonder?.(cevap, Date.now() - baslangic.current)
      .then(() => setGonderimDurumu('bitti'))
      .catch(() => setGonderimDurumu('hata'))
  }

  const reset = () => setResult(null)

  /**
   * Hata ayıklama kancası: `?debug` ile açılır.
   *
   * Üretilen sayfayı ve çözümünü pencereye asar. Destek tarafında
   * "bu sayfa niye böyle çıktı" sorusunu cevaplamanın ve arayüzü
   * otomatik sınamanın tek pratik yolu bu. Adresinde `debug` yoksa
   * hiçbir şey yazılmaz.
   */
  useEffect(() => {
    if (!new URLSearchParams(location.search).has('debug')) return
    const id = 'nirengi-debug'
    let el = document.getElementById(id)
    if (!el) {
      el = document.createElement('script')
      el.id = id
      el.setAttribute('type', 'application/json')
      document.body.appendChild(el)
    }
    // DOM'a yazıyoruz, window'a değil: tarayıcı araçları sayfanın JS
    // global alanını göremiyor ama DOM'u paylaşıyor.
    el.textContent = JSON.stringify(
      instance ? { code, level, mode: modeOf(code!, instance), instance } : null,
    )
  }, [code, level, instance])

  if (!code || !instance) {
    return (
      <div className="menu">
        <h1>Egzersizler</h1>
        <p>20 şablon. Her açılışta yeni bir sayfa üretilir.</p>
        {EXERCISE_CODES.map((c) => (
          <div
            key={c}
            className="card"
            onClick={() => {
              setCode(c)
              setRound((r) => r + 1)
              reset()
            }}
          >
            <div className="t">{EXERCISES[c].title}</div>
            <div className="d">
              {EXERCISES[c].domains.join(', ')} · {EXERCISES[c].channels.join('/')}
            </div>
          </div>
        ))}
        <button onClick={onExit}>Geri</button>
      </div>
    )
  }

  const mode = modeOf(code, instance)

  if (bagli && gonderimDurumu === 'bitti') {
    return (
      <div className="ex">
        <div className="bitti">
          <h2>Bitti, eline sağlık</h2>
          <p>
            {listeVar
              ? 'Çalışman kaydedildi. Listede başka çalışmalar da var.'
              : 'Çalışman kaydedildi. Bu sayfayı kapatabilirsin.'}
          </p>
          {listeVar && (
            <button className="don" onClick={onExit}>
              Listeye dön
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="ex">
      <div className="bar" style={{ padding: '8px 0 12px' }}>
        <span>
          <b>{EXERCISES[code].title}</b>
        </span>
        {!bagli && (
          <span>
            seviye
            <button className="mini" onClick={() => { setLevel((l) => Math.max(1, l - 1)); reset() }}>
              −
            </button>
            <b style={{ margin: '0 8px' }}>{level}</b>
            <button className="mini" onClick={() => { setLevel((l) => Math.min(10, l + 1)); reset() }}>
              +
            </button>
          </span>
        )}
      </div>

      {bagli && gonderimDurumu === 'gidiyor' && <p className="nasil">Kaydediliyor…</p>}
      {bagli && gonderimDurumu === 'hata' && (
        <p className="nasil" style={{ color: '#c0392b' }}>
          Kaydedilemedi. İnternet bağlantını kontrol edip tekrar dene.
        </p>
      )}

      <p className="prompt">{instance.prompt}</p>
      <p className="nasil">{EXERCISE_HINT[code]}</p>

      {instance.spokenPrompt && <Listen text={instance.spokenPrompt} />}

      <Runner
        key={`${code}-${level}-${round}`}
        code={code}
        instance={instance}
        mode={mode}
        result={result}
        onResult={sonucAl}
      />

      {!bagli && (
        <div className="controls">
          <button onClick={() => { setRound((r) => r + 1); reset() }}>Yeni sayfa</button>
          <button onClick={() => { setCode(null); reset() }}>Listeye dön</button>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * İşitsel yönerge
 * ------------------------------------------------------------------ */

function Listen({ text }: { text: string }) {
  const [spoken, setSpoken] = useState(0)
  const [noVoice, setNoVoice] = useState(false)

  const speak = () => {
    if (!('speechSynthesis' in window)) {
      setNoVoice(true)
      return
    }
    window.speechSynthesis.cancel()
    const u = new SpeechSynthesisUtterance(text)
    u.lang = 'tr-TR'
    u.rate = 0.95
    const tr = window.speechSynthesis.getVoices().find((v) => v.lang.startsWith('tr'))
    if (tr) u.voice = tr
    else setNoVoice(true)
    window.speechSynthesis.speak(u)
    setSpoken((n) => n + 1)
  }

  return (
    <div style={{ display: 'grid', gap: 8, margin: '4px 0 14px' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="primary" onClick={speak}>
          ▶ Dinle
        </button>
        <span className="note" style={{ textAlign: 'left' }}>
          {spoken === 0 ? 'Yönergeyi dinlemek için bas.' : `${spoken} kez dinlendi`}
        </span>
      </div>
      {noVoice && (
        <p className="note" style={{ textAlign: 'left', color: 'var(--warn)' }}>
          Bu cihazda Türkçe ses bulunamadı. Uygulayıcı yönergeyi sesli okumalı:
          <br />
          <b>{text}</b>
        </p>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Etkileşim biçimleri
 * ------------------------------------------------------------------ */

function Runner({
  code,
  instance,
  mode,
  result,
  onResult,
}: {
  code: ExerciseCode
  instance: ExerciseInstance
  mode: Mode
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  if (mode === 'cevir') return <FlipGame instance={instance} result={result} onResult={onResult} />
  if (mode === 'kelime') return <WordHunt instance={instance} result={result} onResult={onResult} />
  if (mode === 'dizi') return <SpanRecall instance={instance} result={result} onResult={onResult} />
  if (mode === 'sihirbaz')
    return <Wizard code={code} instance={instance} result={result} onResult={onResult} />
  if (mode === 'cizgi') return <TraceMaze instance={instance} result={result} onResult={onResult} />
  if (mode === 'damgala') return <StampBoard instance={instance} result={result} onResult={onResult} />
  return <MarkOrType code={code} instance={instance} mode={mode} result={result} onResult={onResult} />
}

/* ---------- sihirbaz: tek tek sor ---------- */

/**
 * Maddeleri tek tek sorar: büyük bir soru, altında seçenekler.
 *
 * On iki kelimeyi yan yana kutularla göstermek, çocuğun önce hangi
 * kutuya dokunacağını seçmesini gerektiriyordu; asıl görevin (kelimeyi
 * gruba yerleştirmek) üstüne gereksiz bir yük. Tek tek sorulunca o yük
 * kalkıyor ve her maddeye verilen süre de ayrı ayrı ölçülebilir hale
 * geliyor — ileride hız verisi için gerekli olacak.
 */
function Wizard({
  code,
  instance,
  result,
  onResult,
}: {
  code: ExerciseCode
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const rows = instance.render.find((n) => n.t === 'rows') as
    | Extract<RenderNode, { t: 'rows' }>
    | undefined
  const [at, setAt] = useState(0)
  const [answers, setAnswers] = useState<string[]>([])
  const locked = result !== null

  if (!rows) return null
  const items = rows.items
  const palette = rows.palette ?? []

  if (locked) {
    return (
      <>
        <Score result={result} />
        <div className="erows auto">
          {items.map((it, i) => (
            <div className="erow" key={i}>
              <span className="lb">
                <Uyaran shape={it.labelShape} glyph={it.label} />
              </span>
              <span
                className="slot"
                style={{ color: result.perItem?.[i] ? 'var(--ok)' : 'var(--bad)' }}
              >
                <Dizi metin={answers[i] ?? ''} />
              </span>
            </div>
          ))}
        </div>
      </>
    )
  }

  const choose = (g: string) => {
    const next = [...answers]
    next[at] = g
    setAnswers(next)
    if (at + 1 < items.length) setAt(at + 1)
    else onResult(getExercise(code).grade(instance, next), next)
  }

  return (
    <>
      <p className="note" style={{ textAlign: 'left' }}>
        {at + 1} / {items.length}
      </p>
      <div
        style={{
          minHeight: 110,
          display: 'grid',
          placeItems: 'center',
          background: 'var(--panel)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          margin: '8px 0 16px',
          fontSize: 34,
          fontWeight: 600,
        }}
      >
        <Uyaran shape={items[at]?.labelShape} glyph={items[at]?.label} />
      </div>

      <div className="controls" style={{ borderTop: 'none', padding: 0 }}>
        {palette.map((g) => (
          <button
            key={kimlik(g)}
            className="primary"
            style={{ flex: '1 1 120px' }}
            onClick={() => choose(kimlik(g))}
          >
            <Uyaran {...g} />
          </button>
        ))}
      </div>

      {at > 0 && (
        <div className="controls">
          <button className="mini" onClick={() => setAt(at - 1)}>
            ← Geri
          </button>
        </div>
      )}
    </>
  )
}

/* ---------- dizi: göster, KAPAT, sonra sor ---------- */

/**
 * Dizi hatırlama bir bellek görevi; uyarıcı ekranda kalırsa belleği
 * değil kopyalamayı ölçer. Kağıtta çocuk sayfayı kapatır, ekranda
 * uygulamanın kapatması gerekir.
 *
 * Diziler ayrıca TEK TEK sunuluyor: beşi birden gösterilince çocuk
 * hangisine bakacağını bilemiyor ve görev dizi belleği olmaktan çıkıp
 * tarama görevine dönüşüyor.
 */
function SpanRecall({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const grid = instance.render.find((n) => n.t === 'grid') as
    | Extract<RenderNode, { t: 'grid' }>
    | undefined
  const rowsNode = instance.render.find((n) => n.t === 'rows') as
    | Extract<RenderNode, { t: 'rows' }>
    | undefined

  // Izgara satır satır dizileri taşıyor: son sütun ayraç
  const sequences = useMemo(() => {
    if (!grid) return []
    const out: string[][] = []
    for (let r = 0; r < grid.rows; r++) {
      const row: string[] = []
      for (let c = 0; c < grid.cols - 1; c++) {
        const h = grid.cells[r * grid.cols + c]
        const g = h?.shape ?? h?.glyph
        if (g) row.push(g)
      }
      if (row.length) out.push(row)
    }
    return out
  }, [grid])

  const reversed = instance.prompt.includes('TERSTEN')
  const palette = rowsNode?.palette ?? []

  const [round, setRound] = useState(0)
  const [phase, setPhase] = useState<'hazir' | 'goster' | 'cevapla'>('hazir')
  const [shown, setShown] = useState(-1)
  const [answers, setAnswers] = useState<string[]>([])
  const [current, setCurrent] = useState<string[]>([])
  const locked = result !== null

  const play = async () => {
    setPhase('goster')
    const seq = sequences[round] ?? []
    for (let i = 0; i < seq.length; i++) {
      setShown(i)
      await new Promise((r) => setTimeout(r, 700))
      setShown(-1)
      await new Promise((r) => setTimeout(r, 180))
    }
    setPhase('cevapla')
    setCurrent([])
  }

  const submitRound = () => {
    const next = [...answers]
    next[round] = (reversed ? [...current].reverse() : current).join(' ')
    setAnswers(next)
    setCurrent([])
    if (round + 1 < sequences.length) {
      setRound(round + 1)
      setPhase('hazir')
    } else {
      onResult(getExercise('span_recall').grade(instance, next), next)
    }
  }

  if (locked) {
    return (
      <>
        <Score result={result} />
        <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse' }}>
          <tbody>
            {sequences.map((s, i) => (
              <tr key={i}>
                <td style={{ padding: 6, color: 'var(--muted)' }}>{i + 1}.</td>
                <td style={{ padding: 6 }}>
                  <Dizi metin={s.join(' ')} />
                </td>
                <td
                  style={{
                    padding: 6,
                    color: result.perItem?.[i] ? 'var(--ok)' : 'var(--bad)',
                  }}
                >
                  <Dizi metin={answers[i] ?? ''} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    )
  }

  const seq = sequences[round] ?? []

  return (
    <>
      <p className="note" style={{ textAlign: 'left' }}>
        {round + 1}. dizi / {sequences.length}
        {reversed ? ' · TERSTEN söyleyeceksin' : ''}
      </p>

      <div
        style={{
          minHeight: 120,
          display: 'grid',
          placeItems: 'center',
          background: 'var(--panel)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          margin: '8px 0 14px',
        }}
      >
        {phase === 'hazir' && <span className="note">Hazır olunca başlat</span>}
        {phase === 'goster' && (
          <span className="tek" style={{ fontSize: 64, lineHeight: 1 }}>
            {shown >= 0 ? <UyaranAdi ad={seq[shown] ?? ''} /> : ' '}
          </span>
        )}
        {phase === 'cevapla' && (
          <span style={{ fontSize: 32, letterSpacing: 6 }}>
            <Dizi metin={current.join(' ')} bos="…" />
          </span>
        )}
      </div>

      {phase === 'hazir' && (
        <div className="controls">
          <button className="primary wide" onClick={play}>
            Diziyi göster
          </button>
        </div>
      )}

      {phase === 'cevapla' && (
        <>
          <div className="palette" style={{ position: 'static' }}>
            {palette.map((g) => (
              <button
                key={kimlik(g)}
                className="mini glyph-btn"
                onClick={() => setCurrent((c) => [...c, kimlik(g)])}
              >
                <Uyaran {...g} />
              </button>
            ))}
            <button className="mini" onClick={() => setCurrent((c) => c.slice(0, -1))}>
              ⌫
            </button>
          </div>
          <div className="controls">
            <button className="primary wide" disabled={current.length === 0} onClick={submitRound}>
              {round + 1 < sequences.length ? 'Sonraki dizi' : 'Bitir'}
            </button>
          </div>
        </>
      )}
    </>
  )
}

/* ---------- kelime: iki dokunuşla kelime seç ---------- */

/**
 * Kelime bulmacada hücreleri tek tek işaretletmek yanlıştı: iki kelime
 * bir harfte kesiştiğinde ikinci kelimeyi seçmek o harfi kapatıyor ve
 * birinci kelime bozuluyordu.
 *
 * Doğrusu kelime seçmek: ilk harfe dokun, son harfe dokun; aradaki düz
 * çizgi kelimeyi verir. Kesişen harf iki kelimeye birden ait olabilir.
 */
function WordHunt({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const { words } = instance.solution as { words: string[] }
  const grid = instance.render.find((n) => n.t === 'grid') as
    | Extract<RenderNode, { t: 'grid' }>
    | undefined
  const [anchor, setAnchor] = useState<number | null>(null)
  const [runs, setRuns] = useState<{ word: string; cells: string[] }[]>([])
  const locked = result !== null

  if (!grid) return null
  const cols = grid.cols
  const letters = grid.cells.map((c) => c.glyph ?? '')

  const fold = (s: string) =>
    s.toLocaleLowerCase('tr-TR')
      .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i')
      .replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')

  const lineBetween = (a: number, b: number): number[] | null => {
    const ar = Math.floor(a / cols), ac = a % cols
    const br = Math.floor(b / cols), bc = b % cols
    const dr = Math.sign(br - ar), dc = Math.sign(bc - ac)
    const len = Math.max(Math.abs(br - ar), Math.abs(bc - ac)) + 1
    // Yalnız yatay, dikey ve 45° çapraz
    if (Math.abs(br - ar) !== Math.abs(bc - ac) && br !== ar && bc !== ac) return null
    const out: number[] = []
    for (let i = 0; i < len; i++) out.push((ar + dr * i) * cols + (ac + dc * i))
    return out
  }

  const found = new Set(runs.map((r) => fold(r.word)))
  const marked = new Set(runs.flatMap((r) => r.cells))

  const tap = (i: number) => {
    if (locked) return
    if (anchor === null) {
      setAnchor(i)
      return
    }
    if (anchor === i) {
      setAnchor(null)
      return
    }
    const line = lineBetween(anchor, i)
    setAnchor(null)
    if (!line) return
    const word = line.map((k) => letters[k] ?? '').join('')
    const hit = words.find((w) => fold(w) === fold(word) || fold(w) === fold([...word].reverse().join('')))
    if (!hit || found.has(fold(hit))) return
    const cells = (fold(hit) === fold(word) ? line : [...line].reverse()).map((k) => 'w' + k)
    setRuns((r) => [...r, { word: hit, cells }])
  }

  return (
    <>
      <p className="note" style={{ textAlign: 'left' }}>
        Kelimenin ilk harfine, sonra son harfine dokun.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '6px 0 12px' }}>
        {words.map((w) => (
          <span
            key={w}
            style={{
              fontWeight: 600,
              textDecoration: found.has(fold(w)) ? 'line-through' : 'none',
              color: found.has(fold(w)) ? 'var(--ok)' : 'var(--ink)',
            }}
          >
            {w}
          </span>
        ))}
      </div>
      <div className="egrid" style={{ gridTemplateColumns: `repeat(${cols}, auto)` }}>
        {grid.cells.map((c, i) => (
          <div
            key={i}
            className={
              'c' + (marked.has('w' + i) ? ' sel' : '') + (anchor === i ? ' anchor' : '')
            }
            onClick={() => tap(i)}
          >
            {c.glyph ?? ''}
          </div>
        ))}
      </div>
      <Score result={result} />
      {!locked && (
        <div className="controls">
          <button className="primary wide" onClick={() => onResult(getExercise('word_search').grade(instance, runs), runs)}>
            Kontrol et
          </button>
        </div>
      )}
    </>
  )
}

/* ---------- çizgi: labirentte yol çiz ---------- */

function TraceMaze({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const solution = instance.solution as number[]
  const start = solution[0] ?? 0
  const end = solution[solution.length - 1] ?? 0
  const cols = Number(instance.params['cols'] ?? 8)
  const [path, setPath] = useState<number[]>([start])
  const locked = result !== null

  const grid = instance.render.find((n) => n.t === 'grid') as
    | Extract<RenderNode, { t: 'grid' }>
    | undefined
  if (!grid) return null

  const step = (to: number) => {
    if (locked) return
    const last = path[path.length - 1]!
    // Geri alma: bir önceki kutuya dokunmak son adımı siler
    if (path.length > 1 && to === path[path.length - 2]) {
      setPath(path.slice(0, -1))
      return
    }
    if (path.includes(to)) return

    const diff = to - last
    const sameRow = Math.abs(diff) === 1 && Math.floor(to / cols) === Math.floor(last / cols)
    const sameCol = Math.abs(diff) === cols
    if (!sameRow && !sameCol) return

    // Aradaki duvar kapalıysa geçilemez — kural ekranda da geçerli
    const dir = diff === -cols ? 0 : diff === 1 ? 1 : diff === cols ? 2 : 3
    const walls = grid.cells[last]?.walls
    if (!walls || walls[dir]) return

    setPath([...path, to])
  }

  const done = path[path.length - 1] === end

  return (
    <>
      <p className="note" style={{ textAlign: 'left' }}>
        A'dan başlayıp komşu kutulara dokunarak ilerle. Geri almak için bir önceki
        kutuya dokun.
      </p>
      <div className="egrid maze" style={{ gridTemplateColumns: `repeat(${grid.cols}, auto)` }}>
        {grid.cells.map((c, i) => (
          <div
            key={i}
            className={'c' + (path.includes(i) ? ' trail' : '')}
            style={{
              borderTop: c.walls?.[0] ? '2px solid #38414f' : '2px solid transparent',
              borderRight: c.walls?.[1] ? '2px solid #38414f' : '2px solid transparent',
              borderBottom: c.walls?.[2] ? '2px solid #38414f' : '2px solid transparent',
              borderLeft: c.walls?.[3] ? '2px solid #38414f' : '2px solid transparent',
              width: 22,
              height: 22,
              cursor: 'pointer',
            }}
            onClick={() => step(i)}
          >
            <Uyaran shape={c.shape} glyph={c.glyph} />
          </div>
        ))}
      </div>
      <Score result={result} />
      {!locked && (
        <div className="controls">
          <button className="mini" onClick={() => setPath([start])}>
            Baştan
          </button>
          <button
            className="primary wide"
            disabled={!done}
            onClick={() => onResult(getExercise('maze').grade(instance, path), path)}
          >
            {done ? 'Kontrol et' : 'B’ye ulaş'}
          </button>
        </div>
      )}
    </>
  )
}

function Score({ result }: { result: GradeResult | null }) {
  if (!result) return null
  return (
    <p className="big" style={{ color: result.accuracy > 0.8 ? 'var(--ok)' : 'var(--warn)' }}>
      {result.correct} / {result.total} doğru
    </p>
  )
}

/* ---------- işaretle / yaz ---------- */

function MarkOrType({
  code,
  instance,
  mode,
  result,
  onResult,
}: {
  code: ExerciseCode
  instance: ExerciseInstance
  mode: Mode
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [typed, setTyped] = useState<Record<string, string>>({})
  const [focus, setFocus] = useState<string | null>(null)
  const locked = result !== null

  const toggle = (id: string) => {
    if (locked) return
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const check = () => {
    const answer = mode === 'yaz' ? orderedAnswers(instance, typed) : [...picked]
    onResult(getExercise(code).grade(instance, answer), answer)
  }

  return (
    <>
      {instance.render.map((n, i) => (
        <Node
          key={i}
          node={n}
          picked={picked}
          typed={typed}
          focus={focus}
          locked={locked}
          onToggle={toggle}
          onType={(id, v) => setTyped((t) => ({ ...t, [id]: v }))}
          onFocus={setFocus}
        />
      ))}
      <Score result={result} />
      {!locked && (
        <div className="controls">
          <button className="primary wide" onClick={check}>
            Kontrol et
          </button>
        </div>
      )}
    </>
  )
}

/* ---------- damgala: paletten şekil seç, hücreye bas ---------- */

function StampBoard({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const palette = useMemo(() => {
    if (instance.code === 'sudoku_mini') {
      const size = Number(instance.params['size'] ?? 4)
      return Array.from({ length: size }, (_, i) => String(i + 1))
    }
    const sol = instance.solution as { cell: string; shape: string }[]
    return [...new Set(sol.map((s) => s.shape))]
  }, [instance])
  const [brush, setBrush] = useState<string>(palette[0] ?? '')
  /**
   * Seçili fırça ayrıca bir referansta tutuluyor.
   *
   * Sebebi gerçek bir hata: şekli seçip hemen kutuya basınca hücrenin
   * tıklama işleyicisi hâlâ BİR ÖNCEKİ fırçayı görüyordu — React yeniden
   * çizmeye fırsat bulamadan ikinci dokunuş geliyor. Çocuk hızlı
   * dokunduğunda yanlış sembol basılıyordu ve bunu fark etmesi zor.
   */
  const brushRef = useRef(brush)
  const pick = (g: string) => {
    brushRef.current = g
    setBrush(g)
  }
  const [board, setBoard] = useState<Record<string, string>>({})
  const locked = result !== null

  const grid = instance.render.find((n) => n.t === 'grid') as
    | Extract<RenderNode, { t: 'grid' }>
    | undefined
  if (!grid) return null

  return (
    <>
      <p className="note" style={{ textAlign: 'left' }}>
        Önce aşağıdan birini seç, sonra boş kutuya bas.
      </p>
      <div className="echoices">
        {palette.map((g) => (
          <div
            key={g}
            className={'echoice' + (brush === g ? ' sel' : '')}
            onClick={() => !locked && pick(g)}
          >
            <UyaranAdi ad={g} />
          </div>
        ))}
        <div className="echoice" onClick={() => !locked && pick('')} title="sil">
          ⌫
        </div>
      </div>

      <div className="egrid" style={{ gridTemplateColumns: `repeat(${grid.cols}, auto)` }}>
        {grid.cells.map((c, i) => {
          const id = c.id ?? 'g' + i
          // Verili ipuçları (sudokuda) değiştirilemez ve olduğu gibi durur
          const fixed = !c.id
          return (
            <div
              key={i}
              className={'c' + (board[id] ? ' sel' : '') + (fixed ? ' given' : '')}
              onClick={() => {
                if (locked || fixed) return
                const g = brushRef.current
                setBoard((b) => {
                  const next = { ...b }
                  if (g === '') delete next[id]
                  else next[id] = g
                  return next
                })
              }}
            >
              {fixed ? <Uyaran shape={c.shape} glyph={c.glyph} /> : <UyaranAdi ad={board[id] ?? ''} />}
            </div>
          )
        })}
      </div>

      <Score result={result} />
      {!locked && (
        <div className="controls">
          <button className="primary wide" onClick={() => onResult(getExercise(instance.code).grade(instance, board), board)}>
            Kontrol et
          </button>
        </div>
      )}
    </>
  )
}

/* ---------- çevir: kapalı kartlar ---------- */

function FlipGame({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const deck = instance.solution as string[]
  const [open, setOpen] = useState<number[]>([])
  const [matched, setMatched] = useState<string[]>([])
  const [tries, setTries] = useState(0)
  const timer = useRef<number | null>(null)

  const grid = instance.render.find((n) => n.t === 'grid') as
    | Extract<RenderNode, { t: 'grid' }>
    | undefined

  useEffect(() => {
    return () => {
      if (timer.current) window.clearTimeout(timer.current)
    }
  }, [])

  // Hepsi bulununca kendiliğinden biter: "Kontrol et" beklemeye gerek yok
  useEffect(() => {
    const pairs = new Set(deck).size
    if (matched.length === pairs && pairs > 0 && !result) {
      onResult(getExercise('memory_pairs').grade(instance, matched), matched)
    }
  }, [matched, deck, instance, onResult, result])

  if (!grid) return null

  const flip = (i: number) => {
    if (result) return
    if (open.includes(i) || matched.includes(deck[i]!)) return
    if (open.length === 2) return

    const next = [...open, i]
    setOpen(next)
    if (next.length === 2) {
      setTries((t) => t + 1)
      const [a, b] = next
      if (deck[a!] === deck[b!]) {
        setMatched((m) => [...m, deck[a!]!])
        setOpen([])
      } else {
        timer.current = window.setTimeout(() => setOpen([]), 900)
      }
    }
  }

  return (
    <>
      <p className="note" style={{ textAlign: 'left' }}>
        {matched.length} çift bulundu · {tries} deneme
      </p>
      <div className="egrid" style={{ gridTemplateColumns: `repeat(${grid.cols}, auto)` }}>
        {deck.map((g, i) => {
          const shown = open.includes(i) || matched.includes(g) || result !== null
          return (
            <div
              key={i}
              className={'c card-face' + (shown ? ' up' : '')}
              onClick={() => flip(i)}
            >
              {shown ? g : '?'}
            </div>
          )
        })}
      </div>
      <Score result={result} />
    </>
  )
}

/* ------------------------------------------------------------------ */

function orderedAnswers(instance: ExerciseInstance, typed: Record<string, string>): string[] {
  const ids: string[] = []
  for (const node of instance.render) {
    if (node.t === 'rows') for (const it of node.items) if (it.id) ids.push(it.id)
  }
  return ids.map((id) => typed[id] ?? '')
}

function hashOf(code: string, level: number, round: number): number {
  let h = 2166136261
  const s = `${code}|${level}|${round}`
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function Node({
  node,
  picked,
  typed,
  focus,
  locked,
  onToggle,
  onType,
  onFocus,
}: {
  node: RenderNode
  picked: Set<string>
  typed: Record<string, string>
  focus: string | null
  locked: boolean
  onToggle: (id: string) => void
  onType: (id: string, v: string) => void
  onFocus: (id: string | null) => void
}) {
  switch (node.t) {
    case 'text':
      return (
        <p
          style={{
            fontSize: node.size === 'lg' ? 28 : node.size === 'sm' ? 13 : 16,
            fontWeight: node.bold ? 700 : 400,
          }}
        >
          {node.value}
        </p>
      )

    case 'grid': {
      const isMaze = node.cells.some((c) => c.walls)
      return (
        <div
          className={'egrid' + (isMaze ? ' maze' : '')}
          style={{ gridTemplateColumns: `repeat(${node.cols}, auto)`, gap: node.gap ?? 4 }}
        >
          {node.cells.map((c, i) => {
            const id = c.id ?? 'x' + i
            const style = c.walls
              ? {
                  borderTop: c.walls[0] ? '2px solid #38414f' : '2px solid transparent',
                  borderRight: c.walls[1] ? '2px solid #38414f' : '2px solid transparent',
                  borderBottom: c.walls[2] ? '2px solid #38414f' : '2px solid transparent',
                  borderLeft: c.walls[3] ? '2px solid #38414f' : '2px solid transparent',
                  width: 20,
                  height: 20,
                }
              : undefined
            return (
              <div
                key={i}
                className={'c' + (picked.has(id) ? ' sel' : '') + (c.blank ? ' blank' : '')}
                style={style}
                onClick={() => !locked && c.id && onToggle(id)}
              >
                <Uyaran shape={c.shape} glyph={c.glyph} />
              </div>
            )
          })}
        </div>
      )
    }

    case 'paths': {
      // Etiketler SVG'nin DIŞINDA: içeride olsalardı preserveAspectRatio
      // "none" onları da esnetir ve harfler basık çıkardı.
      const h = node.height ?? 200
      return (
        <div className="pathwrap" style={{ height: h }}>
          {/* Her çizgi önce arka plan renginde kalın bir "hale" ile, sonra
              kendisiyle çiziliyor. Kesişmede sonraki çizginin halesi
              öncekini kesiyor ve hangisinin üstten geçtiği görünüyor —
              hepsi aynı renkte üst üste binince yol takip edilemiyordu. */}
          <svg viewBox="0 0 1000 1000" preserveAspectRatio="none">
            {node.items.map((p, i) => {
              const pts = p.points.map(([x, y]) => `${x * 1000},${y * 1000}`).join(' ')
              return (
                <g key={i}>
                  <polyline
                    points={pts}
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth={10}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                  <polyline
                    points={pts}
                    fill="none"
                    stroke={p.color ?? '#2563c9'}
                    strokeWidth={3}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                </g>
              )
            })}
          </svg>
          {(node.labels ?? []).map((l, i) => (
            <span key={i} className="plabel" style={{ left: `${l.x * 100}%`, top: `${l.y * 100}%` }}>
              {l.text}
            </span>
          ))}
        </div>
      )
    }

    case 'scatter':
      return (
        <div style={{ position: 'relative', height: 160 }}>
          {node.items.map((it, i) => (
            <span
              key={i}
              style={{
                position: 'absolute',
                left: `${it.x * 100}%`,
                top: `${it.y * 100}%`,
                transform: `translate(-50%,-50%) rotate(${it.rot ?? 0}deg)`,
                fontSize: 24,
              }}
            >
              <Uyaran shape={it.shape} glyph={it.glyph} />
            </span>
          ))}
        </div>
      )

    case 'key':
      return (
        <div className="ekey">
          {node.pairs.map((p, i) => (
            <div className="p" key={i}>
              <b>
                <Uyaran shape={p.fromShape} glyph={p.from} />
              </b>
              <span>{p.to}</span>
            </div>
          ))}
        </div>
      )

    case 'rows': {
      const palette = node.palette
      return (
        <>
          {/* Sabit sütun sayısı dar ekranda taşıyordu: sütunlar artık
              yere göre kendiliğinden sarıyor. */}
          <div className="erows auto">
            {node.items.map((it, i) => {
              const id = it.id ?? 'r' + i
              const active = focus === id
              return (
                <div className={'erow' + (active ? ' active' : '')} key={i}>
                  <span className="lb">
                    <Uyaran shape={it.labelShape} glyph={it.label} />
                  </span>
                  {it.blank &&
                    (palette ? (
                      <span className="slot" onClick={() => !locked && onFocus(id)}>
                        <Dizi metin={typed[id] ?? ''} bos={' '} />
                      </span>
                    ) : (
                      <input
                        inputMode="text"
                        disabled={locked}
                        value={typed[id] ?? ''}
                        onFocus={() => onFocus(id)}
                        onChange={(e) => onType(id, e.target.value)}
                      />
                    ))}
                </div>
              )
            })}
          </div>

          {palette && !locked && (
            <div className="palette">
              <div className="note" style={{ textAlign: 'left', width: '100%' }}>
                {focus ? 'Seçili satıra yazılıyor' : 'Önce bir satıra dokun'}
              </div>
              {palette.map((p) => {
                const g = kimlik(p)
                return (
                <button
                  key={g}
                  className="mini glyph-btn"
                  disabled={!focus}
                  onClick={() => {
                    if (!focus) return
                    if (node.paletteMode === 'sequence') {
                      const cur = typed[focus] ?? ''
                      onType(focus, cur ? `${cur} ${g}` : g)
                    } else {
                      // Tek değerli satır: yaz ve kendiliğinden bir sonraki
                      // boş satıra geç — her seferinde satıra dokunmak
                      // gereksiz bir zahmet
                      onType(focus, g)
                      const ids = node.items.map((it, k) => it.id ?? 'r' + k)
                      const at = ids.indexOf(focus)
                      const nextEmpty =
                        ids.slice(at + 1).find((id) => !typed[id]) ??
                        ids.find((id) => !typed[id] && id !== focus)
                      onFocus(nextEmpty ?? null)
                    }
                  }}
                >
                  <Uyaran {...p} />
                </button>
                )
              })}
              <button
                className="mini"
                disabled={!focus}
                onClick={() => {
                  if (!focus) return
                  if (node.paletteMode === 'sequence') {
                    const parts = (typed[focus] ?? '').split(' ').filter(Boolean)
                    parts.pop()
                    onType(focus, parts.join(' '))
                  } else {
                    onType(focus, '')
                  }
                }}
              >
                ⌫
              </button>
            </div>
          )}
        </>
      )
    }

    case 'choices':
      return (
        <div className="echoices">
          {node.items.map((it) => (
            <div
              key={it.id}
              className={'echoice' + (picked.has(it.id) ? ' sel' : '')}
              onClick={() => !locked && onToggle(it.id)}
            >
              <Uyaran shape={it.shape} glyph={it.glyph} />
            </div>
          ))}
        </div>
      )

    /**
     * Desen döşemeleri. Dolu hücre arka planla çiziliyor; aynı matris
     * burada ve A4'te aynı şekli veriyor çünkü arada yazı tipi yok.
     *
     * Seçili döşeme hem çerçeve hem renk alıyor: renk tek başına ipucu
     * taşımıyor (bkz. dokuman/gorsellik.md §0).
     */
    case 'tiles':
      return (
        <div className="etiles">
          {node.items.map((it) => (
            <div
              key={it.id}
              className={
                'etile' +
                (node.pick ? ' pick' : '') +
                (node.pick && picked.has(it.id) ? ' sel' : '')
              }
              onClick={() => node.pick && !locked && onToggle(it.id)}
            >
              <div
                className="tgrid"
                style={{ gridTemplateColumns: `repeat(${node.n}, 1fr)` }}
              >
                {it.on.map((dolu, i) => (
                  <i key={i} className={dolu ? 'on' : ''} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )
  }
}
